import React, { useEffect, useRef } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { logout } from "./store/reducers/user";
import getUserInfoByToken from "./services/user";

import featherIcons from "feather-icons";
import bootstrapBundle from "bootstrap/dist/js/bootstrap.bundle";

//admin pages
import ManageUsersPage from "./pages/AdminPages/ManageUsers";
import ManageModulesPage from "./pages/AdminPages/ManageModules";
import ManageTestsPage from "./pages/AdminPages/ManageTests";
import AddTestPage from './pages/AdminPages/AddTest'
import EditTestPage from './pages/AdminPages/EditTest'
import CreateModule from "./pages/AdminPages/CreateModule";
//refercences pages
import EmployeesInfo from "./pages/ReferencesPages/EmployeesInfo";
import Grades from "./pages/ReferencesPages/Grades";
import Materials from "./pages/ReferencesPages/Materials";
import Results from "./pages/Results";
//other
import Sidebar from "./components/Sidebar";
import Header from "./components/Header";
import LoginPage from "./pages/Login";
import VerifyEmailPage from "./pages/VerifyEmail";
import HomePage from "./pages/Home";
import Profile from "./pages/ProfileInfo";
import ModulesLanding from "./pages/ModulesLanding";
import ModulePage from "./pages/ModulePage";
import Test from "./components/Test";
import TestResult from "./components/Test/Result";
import PageNotFound from "./pages/Errors/PageNotFound";
import TestsRef from "./pages/ReferencesPages/Tests"
import StaffProgress from "./pages/ReferencesPages/StaffProgress";
import ForbiddenPage from "./pages/Errors/ForbiddenPage";
import MainFooter from "./components/ui/Footers/MainFooter";

import { hasPermission, isPublicPath } from "./services/routePermission";
import TestEvaluate from "./pages/TestEvaluate";
// Модул "Комуникация" е временно скрит
// import ChatRoom from "./pages/ChatPage/ChatRoom";
import { fetchCardsInfo, fetchLandingInfo, fetchNotifications } from './store/reducers/data/home'; // import the action
import { fetchModules } from "./store/reducers/data/modules";
import { fetchZbutStatus } from "./store/reducers/data/zbut";
import {
  isZbutLocationAllowed,
  notifyZbutRequired,
  ZBUT_REDIRECT_PATH,
} from "./services/zbut";

function App() {
  window.bootstrap = bootstrapBundle;
  featherIcons.replace();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname: location, search } = useLocation();

  const { user } = useSelector((state) => state.user);
  // Следят се само стойностите, а не целият slice обект.
  const zbutLocked = useSelector((state) => state.zbut.locked);
  const zbutChecked = useSelector((state) => state.zbut.checked);
  const wasZbutLocked = useRef(false);

  useEffect(() => {
    const userInfo = getUserInfoByToken(Cookies.get("accessToken"));
    if (!userInfo && !isPublicPath(location)) {
      dispatch(logout());
      navigate("/login");
    }
  });

  useEffect(() => {
    if (user && user.id) {
      dispatch(fetchModules())
      dispatch(fetchZbutStatus());
    }
  }, [user]);

  // Началните справки се зареждат чак след като "ЗБУТ" е попълнен за деня -
  // докато служителят е заключен, сървърът така или иначе ги отказва.
  useEffect(() => {
    if (!(user && user.id && zbutChecked && !zbutLocked)) return;

    dispatch(fetchCardsInfo());
    dispatch(fetchLandingInfo());
    dispatch(fetchNotifications());

    // След отключване списъкът с модули вече не е ограничен само до "ЗБУТ".
    if (wasZbutLocked.current) dispatch(fetchModules());
  }, [user, zbutChecked, zbutLocked]);

  useEffect(() => {
    if (zbutChecked) wasZbutLocked.current = zbutLocked;
  }, [zbutChecked, zbutLocked]);

  useEffect(() => {
    if (isPublicPath(location)) return;
    const userInfo = getUserInfoByToken(Cookies.get("accessToken"));
    if (userInfo) {
      const hasPerm = hasPermission(location, userInfo.role_name);
      // replace - иначе "Назад" връща към забранената страница и пренасочва отново.
      if (!hasPerm) navigate('/forbidden-page', { replace: true })
    }
  }, [location])

  // Задължителният дневен модул "ЗБУТ" заключва всичко останало.
  useEffect(() => {
    if (isPublicPath(location)) return;
    if (user && user.id) dispatch(fetchZbutStatus());
  }, [location]);

  // Заключеният служител се отваря директно в "ЗБУТ" и се уведомява с
  // плаващо съобщение, което изчезва само след 5 секунди.
  useEffect(() => {
    if (!zbutLocked || isPublicPath(location)) return;

    notifyZbutRequired();

    if (isZbutLocationAllowed(location, search)) return;
    navigate(ZBUT_REDIRECT_PATH);
  }, [zbutLocked, location, search]);

  return (
    <div className="App">
      {!isPublicPath(location)
        ? (
          <>
            <Sidebar />
            {location !== '/module' && location !== '/forbidden-page' && <Header />}
            <main className="main">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/admin/manage-users" element={<ManageUsersPage />} />
                <Route path="/admin/manage-modules" element={<ManageModulesPage />} />
                <Route path="/admin/manage-tests" element={<ManageTestsPage />} />
                <Route path="/admin/manage-tests/add" element={<AddTestPage />} />
                <Route path="/admin/manage-tests/edit" element={<EditTestPage />} />
                <Route path="/admin/manage-modules/create-module" element={<CreateModule />} />
                <Route path="/admin/manage-modules/edit-module" element={<CreateModule />} />
                <Route path="/my-courses" element={<ModulesLanding />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/module" element={<ModulePage />} />
                <Route path="/test" element={<Test />} />
                <Route path="/test/result" element={<TestResult />} />
                <Route path="/test/evaluate" element={<TestEvaluate />} />
                <Route path="/results" element={<Results />} />
                <Route path="/references/grades" element={<Grades />} />
                <Route path="/references/staff-members" element={<EmployeesInfo />} />
                <Route path="/references/materials" element={<Materials />} />
                <Route path="/references/tests" element={<TestsRef />} />
                <Route path="/references/study-progress" element={<StaffProgress />} />
                {/* <Route path="/chat" element={<ChatRoom />} /> */}
                <Route path="/forbidden-page" element={<ForbiddenPage />} />
                <Route path="*" element={<PageNotFound />} />
              </Routes>
              <MainFooter />
            </main>
          </>
        ) : (
          <main>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
            </Routes>
          </main>
        )}

      <ToastContainer />
    </div>
  );
}
export default App;
